import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import PageBreakDividerNode from '../components/gn/blocks/PageBreakDividerNode';
import InkBlockNode from '../components/gn/blocks/InkBlockNode';
import VideoEmbedNode from '../components/gn/blocks/VideoEmbedNode';
import MediaClipNode from '../components/gn/blocks/MediaClipNode';
import MathBlockNode from '../components/gn/blocks/MathBlockNode';
import TutorPublishedRegionNode from '../components/gn/blocks/TutorPublishedRegionNode';

/**
 * Guruji NoteBook node types (spec 046, data-model.md "Document-level model").
 *
 * Storage is view-independent (FR-031): the paged view is driven entirely by pageBreak
 * nodes inside the one document, so canvas⇄pages conversion only inserts/removes those
 * nodes (research Decision 1). The tutor's published content is a *reference* node —
 * tutorPublishedRegion renders the published snapshot at display time and never copies
 * it into the student's document (research Decision 14).
 */

/** Page boundary + navigator label (FR-028..030). Invisible-ish divider in canvas view. */
export const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      label: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-gn-page-break]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-gn-page-break': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PageBreakDividerNode);
  },
});

/**
 * Freehand pen input (FR-002). Strokes are polyline JSON; `interpretedText`/`confidence`
 * cache the multimodal-LLM reading (research Decision 4) and are refreshed only when the
 * strokes change. The ink itself is never restyled or replaced (FR-022).
 */
export const InkBlock = Node.create({
  name: 'inkBlock',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      strokes: { default: '[]' },
      width: { default: 600 },
      height: { default: 200 },
      interpretedText: { default: '' },
      confidence: { default: 0 },
      interpretedAt: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-gn-ink-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-gn-ink-block': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(InkBlockNode);
  },
});

/** Pasted video link → embedded player (FR-003). YouTube only in v1 (research Decision 10). */
export const VideoEmbed = Node.create({
  name: 'videoEmbed',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      provider: { default: 'youtube' },
      videoId: { default: '' },
      originalUrl: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-gn-video-embed]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-gn-video-embed': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VideoEmbedNode);
  },
});

/** User-recorded audio/video clip stored in R2, referenced by asset id (FR-004). */
export const MediaClip = Node.create({
  name: 'mediaClip',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      assetId: { default: 0 },
      mediaType: { default: 'AUDIO' },
      durationSeconds: { default: 0 },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-gn-media-clip]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-gn-media-clip': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MediaClipNode);
  },
});

/** Standalone display equation rendered by KaTeX (FR-020, research Decision 5). */
export const MathBlock = Node.create({
  name: 'mathBlock',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      latex: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-gn-math-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-gn-math-block': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MathBlockNode);
  },
});

/**
 * Read-only region in a student topic-linked GN rendering the tutor's *published* content
 * (FR-035, FR-038). A reference, never a copy: content is fetched from the published-canvas
 * endpoint at display time. Exactly one per topic-linked student GN — save validation
 * rejects documents missing it, so it cannot be deleted (research Decision 14).
 */
export const TutorPublishedRegion = Node.create({
  name: 'tutorPublishedRegion',
  group: 'block',
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      courseId: { default: 0 },
      nodeId: { default: 0 },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-gn-tutor-region]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-gn-tutor-region': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TutorPublishedRegionNode);
  },
});

export const gnNodeExtensions = [
  PageBreak,
  InkBlock,
  VideoEmbed,
  MediaClip,
  MathBlock,
  TutorPublishedRegion,
];
